import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Horizontal pill tabs with sliding indicator. Scrolls on small screens.
 */
export default function SegmentedTabs({
  items,
  activeKey,
  onChange,
  className = "",
}) {
  const containerRef = useRef(null);
  const buttonRefs = useRef([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const updateIndicator = () => {
    const idx = items.findIndex((item) => item.key === activeKey);
    const btn = buttonRefs.current[idx];
    const container = containerRef.current;
    if (!btn || !container) return;
    const rect = btn.getBoundingClientRect();
    const parentRect = container.getBoundingClientRect();
    setIndicator({
      left: rect.left - parentRect.left,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    updateIndicator();
  }, [activeKey, items]);

  useEffect(() => {
    const handleResize = () => updateIndicator();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [items, activeKey]);

  if (!items?.length) return null;

  return (
    <div className={`overflow-x-auto pb-1 -mx-1 scrollbar-hide ${className}`}>
      <div
        ref={containerRef}
        className="relative inline-flex items-center gap-1 rounded-full bg-white border border-line p-1 min-w-max"
      >
        <div
          className="absolute top-1 bottom-1 rounded-full bg-brand transition-all duration-150 ease-out"
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
          }}
        />
        {items.map((item, i) => (
          <button
            key={item.key}
            type="button"
            ref={(el) => {
              buttonRefs.current[i] = el;
            }}
            onClick={() => !item.disabled && onChange(item.key)}
            disabled={item.disabled}
            aria-pressed={activeKey === item.key}
            className={`relative z-10 px-3.5 sm:px-4 min-h-11 rounded-full text-sm font-semibold whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
              activeKey === item.key ? "text-white" : "text-gray-700 hover:text-ink"
            } ${item.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}
